import { buildStatusRows } from './wordleShare';
import { getCanvasCropForZoom } from './zoomPosition';
import {
  normalizeDailyShareZoomLevel,
  normalizeDailyZoomOrigin,
} from './games/dailyZoomConfig';
import {
  DAILY_CARD_NUMBER_START,
  DAILY_CARD_NUMBER_START_DATE_UTC,
  DAILY_GUESS_ROWS,
  DAILY_ZOOM_BOUNDS,
} from '../constants/games/dailyGame';

const STATUS_COLORS = {
  correct: '#538d4e',
  present: '#b59f3b',
  absent: '#3a3a3c',
};
const NEUTRAL_REVEALED_COLOR = '#8b5cf6';

const EMPTY_CELL_COLOR = '#1f2124';
const EMPTY_CELL_BORDER = '#3a3a3c';
const CARD_BG = '#121213';
const CARD_TEXT = '#ffffff';
const CARD_MUTED = '#818384';

const SITE_TEXT = 'Play for free at zoomed-in.com';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const REVEALED_STATUSES = new Set(['correct', 'present', 'absent']);

function loadImageForCanvas(imageUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
}

function canvasToBlob(canvas, type = 'image/png', quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to export image'));
          return;
        }
        resolve(blob);
      },
      type,
      quality,
    );
  });
}

function roundRectPath(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arcTo(x + width, y, x + width, y + radius, radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  ctx.lineTo(x + radius, y + height);
  ctx.arcTo(x, y + height, x, y + height - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
}

function normalizeStatusCell(value) {
  const normalized = String(value || '').toLowerCase();
  return REVEALED_STATUSES.has(normalized) ? normalized : null;
}

function normalizeStatusRows(rows, columnCount, maxRows) {
  const columns = Math.max(1, Number(columnCount) || 5);
  if (!Array.isArray(rows)) return [];

  return rows.slice(0, Math.max(1, Number(maxRows) || 1)).map((row) =>
    Array.from({ length: columns }, (_, index) => normalizeStatusCell(row?.[index])),
  );
}

export function buildDailyCardDateLabel(dateUtc) {
  if (dateUtc) {
    const parsed = new Date(`${dateUtc}T00:00:00Z`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }
  }

  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function parseUtcDate(dateUtc) {
  if (!dateUtc) return null;
  const parsed = new Date(`${dateUtc}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function getTodayUtcDateStart() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

export function buildDailyCardNumber(dateUtc) {
  const start = parseUtcDate(DAILY_CARD_NUMBER_START_DATE_UTC);
  const target = parseUtcDate(dateUtc) || getTodayUtcDateStart();
  if (!start) return DAILY_CARD_NUMBER_START;

  const diffDays = Math.floor((target.getTime() - start.getTime()) / MS_PER_DAY);
  return Math.max(DAILY_CARD_NUMBER_START, DAILY_CARD_NUMBER_START + diffDays);
}

export async function createDailyPrivateShareCardBlob({
  imageUrl,
  guesses = [],
  solution = '',
  puzzleDateUtc = '',
  zoomLevel = 100,
  zoomOrigin = null,
  includeGuessGrid = true,
  neutralGuessColors = false,
  maxGuesses = DAILY_GUESS_ROWS,
  cardWidth = 560,
  zoomBounds = DAILY_ZOOM_BOUNDS,
  siteText = SITE_TEXT,
  titleText = '',
  metaText = '',
  statusRowsOverride = null,
  gridColumns = 5,
} = {}) {
  if (!imageUrl) throw new Error('Missing imageUrl');

  const img = await loadImageForCanvas(imageUrl);
  const imageWidth = img.naturalWidth || img.width;
  const imageHeight = img.naturalHeight || img.height;
  if (!imageWidth || !imageHeight) throw new Error('Image has invalid dimensions');

  const resolvedMaxGuesses = Math.max(1, Number(maxGuesses) || DAILY_GUESS_ROWS);
  const resolvedGridColumns = Math.max(1, Number(gridColumns) || 5);
  const shouldRenderGuessGrid = includeGuessGrid !== false;
  const fallbackRows = shouldRenderGuessGrid
    ? buildStatusRows({ guesses, solution }).slice(0, resolvedMaxGuesses)
    : [];
  const providedRows = Array.isArray(statusRowsOverride) ? statusRowsOverride : fallbackRows;
  const normalizedRows = shouldRenderGuessGrid
    ? normalizeStatusRows(providedRows, resolvedGridColumns, resolvedMaxGuesses)
    : [];
  const rows = shouldRenderGuessGrid
    ? Array.from(
        { length: resolvedMaxGuesses },
        (_, index) => normalizedRows[index] || Array(resolvedGridColumns).fill(null),
      )
    : [];
  const dailyCardNumber = buildDailyCardNumber(puzzleDateUtc);
  const resolvedTitleText = String(titleText || `Zoomed In Daily #${dailyCardNumber}`);
  const resolvedMetaText = String(metaText || buildDailyCardDateLabel(puzzleDateUtc));

  const pad = Math.round(cardWidth * 0.06);
  const cardRadius = Math.round(cardWidth * 0.03);
  const mediaSize = Math.round(cardWidth * 0.64);
  const mediaRadius = Math.round(cardWidth * 0.025);

  const headerFontSize = Math.round(cardWidth * 0.06);
  const dateFontSize = Math.round(cardWidth * 0.033);
  const footerFontSize = Math.round(cardWidth * 0.028);

  const cellSize = Math.round(cardWidth * 0.082);
  const cellGap = Math.max(3, Math.round(cellSize * 0.11));
  const gridWidth = cellSize * resolvedGridColumns + cellGap * Math.max(0, resolvedGridColumns - 1);
  const gridHeight =
    rows.length > 0 ? cellSize * rows.length + cellGap * Math.max(0, rows.length - 1) : 0;

  const gapAfterImage = Math.round(cardWidth * 0.035);
  const gapAfterTitle = Math.round(cardWidth * 0.018);
  const gapAfterDate = Math.round(cardWidth * 0.03);
  const gapAfterGrid = Math.round(cardWidth * 0.03);
  const gapWithoutGrid = Math.round(cardWidth * 0.045);
  const bottomPad = Math.round(cardWidth * 0.055);

  const cardHeight =
    pad +
    mediaSize +
    gapAfterImage +
    headerFontSize +
    gapAfterTitle +
    dateFontSize +
    gapAfterDate +
    gridHeight +
    (shouldRenderGuessGrid ? gapAfterGrid : gapWithoutGrid) +
    footerFontSize +
    bottomPad;

  const canvas = document.createElement('canvas');
  canvas.width = cardWidth;
  canvas.height = cardHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  // Fill the full canvas first so PNG corners are opaque (no white triangles on open/share).
  ctx.fillStyle = CARD_BG;
  ctx.fillRect(0, 0, cardWidth, cardHeight);

  ctx.fillStyle = CARD_BG;
  roundRectPath(ctx, 0, 0, cardWidth, cardHeight, cardRadius);
  ctx.fill();

  let y = pad;

  const sourceSquare = Math.min(imageWidth, imageHeight);
  const sourceX = (imageWidth - sourceSquare) / 2;
  const sourceY = (imageHeight - sourceSquare) / 2;
  const resolvedZoomLevel = normalizeDailyShareZoomLevel(zoomLevel, zoomBounds?.initialZoom);
  const resolvedZoomOrigin = normalizeDailyZoomOrigin(zoomOrigin);
  const { cropX, cropY, cropSize } = getCanvasCropForZoom(
    sourceSquare,
    resolvedZoomLevel,
    resolvedZoomOrigin,
    zoomBounds || DAILY_ZOOM_BOUNDS,
  );

  const mediaX = Math.round((cardWidth - mediaSize) / 2);

  ctx.save();
  roundRectPath(ctx, mediaX, y, mediaSize, mediaSize, mediaRadius);
  ctx.clip();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    img,
    sourceX + cropX,
    sourceY + cropY,
    cropSize,
    cropSize,
    mediaX,
    y,
    mediaSize,
    mediaSize,
  );
  ctx.restore();

  y += mediaSize + gapAfterImage;

  ctx.fillStyle = CARD_TEXT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.font = `800 ${headerFontSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.fillText(resolvedTitleText, cardWidth / 2, y);

  y += headerFontSize + gapAfterTitle;

  ctx.fillStyle = CARD_MUTED;
  ctx.font = `600 ${dateFontSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.fillText(resolvedMetaText, cardWidth / 2, y);

  y += dateFontSize + gapAfterDate;

  if (shouldRenderGuessGrid && rows.length > 0) {
    const gridX = Math.round((cardWidth - gridWidth) / 2);
    rows.forEach((row, rowIndex) => {
      row.forEach((status, colIndex) => {
        const x = gridX + colIndex * (cellSize + cellGap);
        const cellY = y + rowIndex * (cellSize + cellGap);
        const radius = Math.round(cellSize * 0.14);
        const isRevealedCell = status === 'correct' || status === 'present' || status === 'absent';
        const shouldNeutralizeReveal =
          neutralGuessColors && (status === 'correct' || status === 'present');
        const hasFilledState = shouldNeutralizeReveal || isRevealedCell;

        roundRectPath(ctx, x, cellY, cellSize, cellSize, radius);
        if (shouldNeutralizeReveal) {
          ctx.fillStyle = NEUTRAL_REVEALED_COLOR;
        } else if (isRevealedCell) {
          ctx.fillStyle = STATUS_COLORS[status] || EMPTY_CELL_COLOR;
        } else {
          ctx.fillStyle = EMPTY_CELL_COLOR;
        }
        ctx.fill();
        if (!hasFilledState) {
          ctx.strokeStyle = EMPTY_CELL_BORDER;
          ctx.lineWidth = Math.max(1, Math.round(cellSize * 0.06));
          ctx.stroke();
        }
      });
    });
  }

  y += gridHeight + (shouldRenderGuessGrid ? gapAfterGrid : gapWithoutGrid);

  ctx.fillStyle = CARD_MUTED;
  ctx.font = `600 ${footerFontSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.fillText(String(siteText || SITE_TEXT), cardWidth / 2, y);

  return canvasToBlob(canvas, 'image/png');
}
